import { DependencyInjectId } from "@@/package/const/DependencyInjectId";
import HttpClient from "@@/package/network/HttpClient";
import { type AxiosResponse } from "axios";
import { type ApplicationContextInterface } from "@@/package/application/applicationContext";
import type UserAccountRequest from "~~/package/apis/user/request/UserAccountRequest";
import type UserV2Response from "~~/package/apis/user/response/UserV2Response";
import { CountryModel } from "~~/package/mapper/user/model/CountryModel";
import type CustomResponse from "~~/package/types/ResponseType";
import { UserDashboardSummaryModel } from "~~/package/mapper/user/model/UserDashboardSummaryModel";
import {UserPasswordChangeModel} from '~~/package/mapper/user/model/UserPasswordChangeModel';
import type {ResendEmailType} from '~~/package/mapper/user/model/ResendEmailType';

export default class UserApi {

    protected http: HttpClient;

    constructor(context: ApplicationContextInterface) {
        this.http = context.getService<HttpClient>(DependencyInjectId.HttpClient);
    }

    public setXCountry(): Promise<AxiosResponse> {
        // baseURL 기준 루트 경로 — live: https://api-cf.../ (동일), local: /__api/ (이중 결합 방지)
        return this.http.get<AxiosResponse>('/');
    }

    public getUserV2(loginTime: number): Promise<AxiosResponse<CustomResponse<UserV2Response>>> {
        return this.http.get<AxiosResponse<CustomResponse<UserV2Response>>>('/v2/user', {time: loginTime}, true);
    }

    public signIn(email: string, password: string): Promise<AxiosResponse<CustomResponse<string>>> {
        return this.http.post<AxiosResponse<CustomResponse<string>>, {[key: string]: string}>('/user/signIn', {email, password}, false);
    }

    public signUp(email: string, password: string, userType: string): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.post<AxiosResponse<CustomResponse<boolean>>, {[key: string]: string}>('/v2/user/signUp',{email, password, userType}, false);
    }

    public userAuthEmail(userId: number, token: string): Promise<AxiosResponse<CustomResponse<string>>> {
        return this.http.get<AxiosResponse<CustomResponse<string>>>('/user/auth/email',{userId, token}, false);
    }

    public userResendEmail(email: string, mailType: ResendEmailType, assessmentId?: number): Promise<AxiosResponse<CustomResponse<boolean>>> {
        const payload = {
            email,
            mailType,
            ...(assessmentId && {
                additionalData: { assessmentId }
            })
        };
        return this.http.post<AxiosResponse<CustomResponse<boolean>>, typeof payload>('/mail/resend', payload, true);
    }

    public userSendAuthEmail(email: string): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.post<AxiosResponse<CustomResponse<boolean>>, {[key: string]: string}>('/mail/auth/email',{email}, false);
    }

    public userPasswordChangeAuthEmail(userId: number, token: string): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.get<AxiosResponse<CustomResponse<boolean>>>('/user/auth/passwordChange',{userId, token}, false);
    }

    public userSendPasswordChangeAuthEmail(email: string): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.post<AxiosResponse<CustomResponse<boolean>>, {[key: string]: string}>('/mail/change/password',{email}, false);
    }

    public userPasswordChange(userId: number, token: string, newPassword: string): Promise<AxiosResponse<CustomResponse<UserPasswordChangeModel>>> {
        return this.http.put<AxiosResponse<CustomResponse<UserPasswordChangeModel>>, {[key: string]: string|number }>('/v2/user/change/password/token',{userId, token, newPassword}, false);
    }

    public userAccountSetting(userAccountRequest: UserAccountRequest): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.patch<AxiosResponse<CustomResponse<boolean>>, UserAccountRequest>('/v2/user', userAccountRequest, true);
    }

    public updateUserTrackSkipFlag(trackSkipFlag: {[key: string]: boolean}): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.put<AxiosResponse<CustomResponse<boolean>>, {[key: string]: boolean}>('/user/track/select/exposureFlag', trackSkipFlag, true);
    }

    public getCountryList(isToContactUs: boolean): Promise<AxiosResponse<CustomResponse<CountryModel[]>>> {
        return this.http.get<AxiosResponse<CustomResponse<CountryModel[]>>>(isToContactUs ? '/countryRegion/list/contact/us' : '/countryRegion/list', undefined, true);
    }

    public setUserCountryData(userId: string, countryRegionId: string): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.post<AxiosResponse<CustomResponse<boolean>>, {[key: string]: string}>('/countryRegion/user/save', {userId, countryRegionId}, true);
    }

    public setMarketingTerms(allowMarketing: boolean): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.put<AxiosResponse<CustomResponse<boolean>>, {[key: string]: string}>('/user/select/marketingFlag', { marketingFlag: allowMarketing.toString() }, true);
    }

    public setUserLocale(locale: string): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.patch<AxiosResponse<CustomResponse<boolean>>, {[key: string]: string}>('/v2/user', {locale}, true);
    }

    public changePassword(newPassword: string, oldPassword?: string): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.put<AxiosResponse<CustomResponse<boolean>>, {[key: string]: string | undefined }>('/v2/user/password',{newPassword, oldPassword}, true);
    }

    public deleteAccount(): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.delete<AxiosResponse<CustomResponse<boolean>>>('/user/withdrawal',{}, true);
    }

    public getUserDashboardSummary(): Promise<AxiosResponse<CustomResponse<UserDashboardSummaryModel>>> {
        return this.http.get<AxiosResponse<CustomResponse<UserDashboardSummaryModel>>>('/dashboard/summary', undefined, true);
    }

    public getBlockedEmailDomains(): Promise<AxiosResponse<CustomResponse<string[]>>> {
        return this.http.get<AxiosResponse<CustomResponse<string[]>>>('/blacklisted-email-domains', undefined, false);
    }

    // SEC-1 (TSK-10568): server-side AccessToken cookie cleanup.
    public signOut(): Promise<AxiosResponse<void>> {
        return this.http.post<AxiosResponse<void>, Record<string, never>>('/user/signOut', {}, false);
    }

    // SEC-1 (TSK-10568): short-lived (60s) token for Proctive desktop deeplink.
    public getDeeplinkToken(): Promise<AxiosResponse<CustomResponse<string>>> {
        return this.http.get<AxiosResponse<CustomResponse<string>>>('/user/deeplink-token', undefined, true);
    }
}
